<template>
  <Component
    :is="component"
    :key="langTo"
    :external="isRouterIgnore"
    :to="langTo"
    :aria-current-value="ariaCurrentValue"
    :target="target"
    rel="noopener"
    @click="putStatsIfOutbound"
    class="base-link"
  >
    <slot />
    <BaseIcon
      v-if="icon"
      class="icon"
      :name="icon"
      :size="iconSize"
      :alt="isExternal ? ls(dapLangGlobal.externalSite) : ''"
    />
  </Component>
</template>

<script lang="ts" setup>
import { NuxtLink } from '#components'
import { dapLangGlobal } from '#imports'
import type { SVG_NAMES } from '~/components/icons'
const { isWebParts } = useWebParts()

const { langPath, langUrl, ls } = useI18n()
const route = useRoute()
const localSearchStore = useLocalSearchStore()

interface Props {
  to?: any
  tag?: string
  ariaCurrentValue?: any
  showIcon?: boolean
  ignoreLang?: boolean
  disableStats?: boolean
  opentype?: 'same' | 'tab'
  nolink?: boolean
  searchscroll?: boolean
  iconSize?: `size-${number}` | 'size-full' | 'size-none'
  samePageForceReload?: boolean
  stop?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  ariaCurrentValue: null,
  showIcon: true,
  ignoreLang: false,
  opentype: 'same',
  nolink: false,
  searchscroll: false,
  samePageForceReload: false,
  stop: false,
})

const path = computed<string>(() => {
  return ((typeof props.to === 'string' ? props.to : props.to?.path) || '').trim()
})

const isExternal = computed<boolean>(() => {
  return /^https?:\/\/.+/.test(path.value)
})

const component = computed(() => {
  if (props.tag) return props.tag
  if (!props.to || props.nolink) return 'span'
  return NuxtLink
})

const target = computed<string>(() => {
  if (isExternal.value || props.opentype === 'tab') {
    return '_blank'
  } else if (isWebParts.value) {
    return '_top'
  }
  return null
})

const icon = computed<SVG_NAMES>(() => {
  if (!props.showIcon) {
    return null
  }
  if (isExternal.value) {
    return 'mdi/open-in-new'
  }
  return null
})

//  VueRouterの管理外にしたいURLのprefix
const ignoreUrlPrefix = ['/thumbnail', '/api', '/file', '/rss', '/static']
const isRouterIgnore = computed<boolean>(() => {
  try {
    if (path.value?.match(/^https?/) || path.value?.match(/^mailto:/)) {
      return true
    }
    return ignoreUrlPrefix.some(u => path.value?.startsWith(u))
  } catch {
    //  ページ内リンク等も存在するため、例外時は明示的にエラーを潰す
    return false
  }
})

const langTo = computed(() => {
  if (!isRouterIgnore.value && !props.ignoreLang) {
    if (typeof props.to === 'string') {
      if (!props.to?.startsWith('#')) {
        return langPath(props.to)
      }
    } else if (!props.to?.path?.startsWith('#')) {
      return { ...props.to, path: langPath(props.to) }
    }
  }
  if (typeof props.to === 'string' && props.to?.startsWith('#')) {
    // ハッシュリンク指定時にURLに含まれるクエリがクリアされてしまうため
    return { hash: props.to, query: route.query }
  }
  return langUrl(props.to)
})

const putStatsIfOutbound = (event) => {
  if (props.stop) {
    event.stopPropagation()
  }
  searchScrollEvent(event)
  if (props.samePageForceReload && route.path === langTo.value) {
    // 同じページ(URL)の場合に強制再読み込みを実施する
    event.stopImmediatePropagation()
    window.location.href = langTo.value
  }
  const innerPfs = ['/', '#']
  if (
    props.disableStats ||
    innerPfs.some(p => {
      if (typeof langTo.value === 'string') {
        return (langTo.value as string)?.startsWith(p)
      } else {
        return !!langTo.value?.hash
      }
    })
  ) {
    return
  }
}

const guardEvent = (e) => {
  // don't redirect with control keys
  if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return
  // don't redirect when preventDefault called
  // if (e.defaultPrevented) return
  // don't redirect on right click
  if (e.button !== undefined && e.button !== 0) return
  // don't redirect if `target='_blank'`
  if (e.currentTarget && e.currentTarget.getAttribute) {
    const target = e.currentTarget.getAttribute('target')
    if (/\b_blank\b/i.test(target)) return
  }
  // this may be a Weex event which doesn't have this method
  // if (e.preventDefault) {
  //   e.preventDefault()
  // }
  return true
}

const searchScrollEvent = (event) => {
  if (guardEvent(event)) {
    if (props.searchscroll) {
      localSearchStore.globalScrollAfterSearch = true
    }
  }
}
</script>

<style lang="scss" scoped>
.base-link {
  overflow-wrap: anywhere;

  .icon {
    vertical-align: middle;
    margin-left: 0.25rem;
  }
}
</style>